import * as Actions from 'actions';
import * as ActionTypes from 'constants';
import { takeEvery , takeLatest, delay } from 'redux-saga';
import { take, call, put, select, cancel, fork, throttle, all } from 'redux-saga/effects';
import _ from 'lodash';
import { getMapInstance } from 'utils';

import * as Api from 'api';
let fetchClientsThrottleTask = null;

/***************************** Selectors ************************************/
export const getClients = state => (state.clients);
export const getRouting = state => (state.routing);

/***************************** Subroutines ************************************/
function * fetchPrevClients() {
  try {
    const clients = yield select(getClients);
    const payload = {
      options: {
        filters: clients.filters,
        sort: clients.sort_column,
        size: clients.size,
        offset: clients.offset - 1
      }
    };
    if(clients.offset - 1 > -1) {
      yield call(prepClientsFetch, { payload });
    }
  }
  catch(e) {
    yield put({ type: ActionTypes.FETCH_CLIENTS_FAILED, payload: { message: e.message, status: e.status }});
  }
}

function * fetchNextClients() {
  try {
    const clients = yield select(getClients);
    const maxOffset = Math.floor(clients.total / clients.size);
    const payload = {
      options: {
        filters: clients.filters,
        sort: clients.sort_column,
        size: clients.size,
        offset: clients.offset + 1
      }
    };
    if(clients.offset < maxOffset) {
      yield call(prepClientsFetch, { payload });
    }
  }
  catch(e) {
    yield put({ type : ActionTypes.FETCH_CLIENTS_FAILED, payload : { message : e.message, status : e.status }})
  }
}

function * fetchSortedClients(action) {
  try {
    yield put(Actions.clearClients());

    const clients = yield select(getClients);
    const payload = { options: { sort : action.payload.column } };
    yield call(prepClientsFetch, { payload });
  }
  catch(e) {
    console.log(e)
  }
}

function * updateTablePageSize({ payload: size }) {
  try {
    const payload = { options: { size } };
    yield put(Actions.setClientPageSize(size));
    yield call(prepClientsFetch, { payload });
  }
  catch(ex) {
    yield call(eventFailed, { payload: ex });
  }
}

function * updateFilters({ payload }) {
  const { column_id, input } = payload;
  try {
    const { filters } = yield select(getClients);
    let updated_filter_data;
    if(!input) {
      updated_filter_data = filters.filter(e => e.type != column_id);
    }
    else if(filters.find(e => e.type == column_id)) {
      updated_filter_data = filters.map(e => e.type == column_id ? { type : column_id, value : input }  : e);
    }
    else {
      updated_filter_data = [ ...filters, { type : column_id, value : input } ];
    }
    const payload = { options: { filters: updated_filter_data } };
    yield call(prepClientsFetch, { payload });
  }
  catch(ex) {
    yield call(eventFailed, { payload: ex, notify: false });
  }
}

function * clearFilters() {
  yield call(prepClientsFetch, { payload: { options: { filters: [] } } });
}

function * updateClientsBounds({ payload: bounds }) {
  try {
    yield call(prepClientsFetch, { payload: { options: { bounds } } });
  }
  catch(ex) {
    console.log(ex);
  }
}

function * toggleFilterClientsByMapBounds({ payload: bool }) {
  const payload = { options: { filter_clients_by_map_bounds: bool } };
  yield call(prepClientsFetch, { payload });
}

function * prepClientsFetch(action) {
  try {
    const options = action && action.payload && action.payload.options ? action.payload.options : {};
    const {
      bounds,
      filters,
      sort_column,
      total,
      offset,
      size,
      filter_table_by_map_bounds
    } = options;
    // yield put(Actions.clearClients());
     const clients = yield select(getClients);
     options.bounds = bounds || clients.bounds;
     options.filters = filters || (clients.filters || []);
     options.sort_column = sort_column || clients.sort_column;
     options.total = total || clients.total;
     if(options.filter_table_by_map_bounds != null) {
       options.offset = 0;
     }
     else {
       options.offset = offset != null ? offset : clients.offset;
     }
     options.size = size || clients.size;
     options.filter_table_by_map_bounds = filter_table_by_map_bounds || clients.filter_table_by_map_bounds;

    yield put(Actions.fetchClientsWithFilters({ ...options }));
  }
  catch(e) {
    yield put(Actions.fetchClientsFailed);
  }
}

function * zoomToClient({ payload: client_id }) {
  try {
    const { clients_table_list, clients_map_list, clients_table_open_state } = yield select(getClients);
    const client = [ ...clients_table_list, ...clients_map_list ].find(c => c._id == client_id);
    if(
      client &&
      client.address &&
      client.address.geocoded
    ) {
      const map = getMapInstance();
      const position = {
        lat: client.address.feature.geometry.coordinates[1],
        lng: client.address.feature.geometry.coordinates[0]
      };
      if(localStorage) {
        localStorage.setItem('map_zoom', 17);
      }
      map.setZoom(17);
      map.panTo(position);
      yield put(Actions.selectClient(client_id));
      if(clients_table_open_state == 'open') {
        yield put(Actions.updateClientsTableOpenState('half_open'));
      }
    }
    else {
      yield put(Actions.notify(
        { message: 'The selected client does not have a location stored in our system' },
        { type: 'warning' }
      ));
    }

  }
  catch(ex) {
    yield call(eventFailed, { payload: ex });
  }
}

function * fetchClientsWithFilters(action) {
  try {
    const { options } = action.payload;
    const { pathname } = (yield select(getRouting)).locationBeforeTransitions;
    let table_clients, map_clients;
    const payload = _.omit(options, [ 'filter_clients_by_map_bounds' ]);
    yield put(Actions.toggleFetchingClients(true));
    if(/^clients\/map/.test(pathname)) {
      let fetched_clients;
      if(options.filter_table_by_map_bounds) {
        fetched_clients = yield all([
          call(Api.fetchClientsWithFilters, { ...payload, size: 5 }),   // table clients
          call(Api.fetchClientsWithFilters, _.omit(payload, [ 'size', 'offset' ]))    // map clients
        ]);
      }
      else {
        fetched_clients = yield all([
          call(Api.fetchClientsWithFilters, _.omit({ ...payload, size: 5 }, [ 'bounds' ])),   // table clients
          call(Api.fetchClientsWithFilters, _.omit(payload, [ 'size', 'offset' ]))    // map clients
        ]);
      }
      if(fetched_clients[0].error && fetched_clients[0].status == 401) {
        yield put(Actions.toggleJobNimbusAuthError(true));
      }
      else {
        const fetched_clients_data = {
          ...(_.omit(fetched_clients[0], [ 'results' ])),
          clients_table_list: fetched_clients[0].results,
          clients_map_list: fetched_clients[1].results
        };
        yield put(Actions.fetchClientsSucceeded(fetched_clients_data));
      }
    }
    else {
      const fetched_clients = yield call(Api.fetchClientsWithFilters, payload || {});
      const fetched_clients_data = {
        ...(_.omit(fetched_clients, [ 'results' ])),
        clients_table_list: fetched_clients.results
      };
      yield put(Actions.fetchClientsSucceeded(fetched_clients_data));
    }
  }
  catch(e) {
    console.log(e);
    yield put({ type : ActionTypes.FETCH_CLIENTS_FAILED , payload : { message : e.message, status : e.status }})
  }
}

function * eventFailed({ payload, notify }) {
  console.log(payload);
  if(notify !== false) {
    yield put(Actions.notify(payload.message || payload));
  }
}

/******************************************************************************/
/******************************* WATCHERS *************************************/
/******************************************************************************/

export function* fetchClientsWithFiltersWatcher() {
  yield* [
    throttle(2000, ActionTypes.FETCH_CLIENTS_WITH_FILTERS, fetchClientsWithFilters),
    takeLatest(ActionTypes.FETCH_NEXT_CLIENTS, fetchNextClients),
    takeLatest(ActionTypes.FETCH_PREV_CLIENTS, fetchPrevClients),
    takeLatest(ActionTypes.FETCH_CLIENTS, prepClientsFetch),
    takeLatest(ActionTypes.SET_CLIENT_SORT_COLUMN, fetchSortedClients),
    takeLatest(ActionTypes.UPDATE_TABLE_PAGE_SIZE, updateTablePageSize),
    takeLatest(ActionTypes.UPDATE_CLIENTS_FILTERS, updateFilters),
    takeLatest(ActionTypes.CLEAR_CLIENTS_FILTERS, clearFilters),
    takeLatest(ActionTypes.UPDATE_CLIENTS_BOUNDS, updateClientsBounds),
    takeLatest(ActionTypes.TOGGLE_FILTER_CLIENTS_TABLE_BY_MAP_BOUNDS, toggleFilterClientsByMapBounds),
    takeLatest(ActionTypes.ZOOM_TO_CLIENT, zoomToClient)
  ]
}



// WEBPACK FOOTER //
// ./src/common/sagas/clients.js